import React, { useMemo } from "react"
import { Table, Tag, Space, Typography, message } from "@mimber/ui-web"
import {
  MailOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
} from "@mimber/ui-web"
import { useInvitations } from "@mimber/client/mimber-organization"
import type { ColumnsType, TableProps } from "@mimber/ui-web"
import type {
  InvitationWithRelationsDto,
  InvitationStatus,
} from "@mimber/schema"
import type { InvitationsTableProps } from "./InvitationsTable.types"
import {
  handleTableChange,
  applyQueryParamsToColumns,
  getColumnSearchProps,
  formatTableDate,
} from "@mimber/ui-web"
import { InvitationActionsDropdown } from "@/components/dropdowns"
import { useRememberedTableQueryParams } from "@/core/hooks"
import {
  getRememberedPaginationConfig,
  getRememberedTableLocale,
} from "@/utils"

const { Text } = Typography

const STATUS_COLORS: Record<InvitationStatus, string> = {
  PENDING: "processing",
  ACCEPTED: "success",
  EXPIRED: "default",
  REVOKED: "error",
}

const STATUS_ICONS: Record<InvitationStatus, React.ReactNode> = {
  PENDING: <ClockCircleOutlined />,
  ACCEPTED: <MailOutlined />,
  EXPIRED: <CloseCircleOutlined />,
  REVOKED: <CloseCircleOutlined />,
}

export const InvitationsTable: React.FunctionComponent<
  InvitationsTableProps
> = ({ onRowClick, className, ...tableProps }) => {
  const { queryParams, setQueryParams, resetQueryParams } =
    useRememberedTableQueryParams({
      tableId: "invitations",
      defaultQueryParams: {
        page: 1,
        limit: 10,
      },
    })

  const { data: response, isLoading, error } = useInvitations(queryParams)

  // Show error message if query fails
  React.useEffect(() => {
    if (error) {
      message.error(`Failed to load invitations: ${error.message}`)
    }
  }, [error])

  const columns: ColumnsType<InvitationWithRelationsDto> = useMemo(
    () => [
      {
        title: "Name",
        key: "name",
        render: (_, record) => <Text strong>{record.name}</Text>,
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search by name"),
      },
      {
        title: "Email",
        dataIndex: "email",
        key: "email",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        ...getColumnSearchProps("Search by email"),
      },
      {
        title: "Tier",
        key: "tier",
        render: (_, record) =>
          record.tier ? (
            <Tag color="blue">{record.tier.name}</Tag>
          ) : (
            <Text type="secondary">—</Text>
          ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (status: InvitationStatus) => (
          <Tag color={STATUS_COLORS[status]} icon={STATUS_ICONS[status]}>
            {status}
          </Tag>
        ),
        filters: [
          { text: "Pending", value: "PENDING" },
          { text: "Accepted", value: "ACCEPTED" },
          { text: "Expired", value: "EXPIRED" },
          { text: "Revoked", value: "REVOKED" },
        ],
      },
      {
        title: "Invited By",
        key: "invitedBy",
        render: (_, record) => {
          const user = record.invitedByMembership?.user
          if (!user) return <Text type="secondary">—</Text>
          return (
            <Text>
              {user.firstName} {user.lastName}
            </Text>
          )
        },
      },
      {
        title: "Sent",
        dataIndex: "sentAt",
        key: "sentAt",
        sorter: true,
        sortDirections: ["ascend", "descend"],
        render: (sentAt: string) => (
          <Space>
            <ClockCircleOutlined />
            <Text>{formatTableDate(sentAt)}</Text>
          </Space>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 80,
        onCell: () => ({
          onClick: (e: React.MouseEvent) => e.stopPropagation(),
        }),
        render: (_, record) => (
          <InvitationActionsDropdown invitation={record} />
        ),
      },
    ],
    []
  )

  const handleRowClick = (record: InvitationWithRelationsDto) => {
    if (onRowClick) {
      onRowClick(record)
    }
  }

  const onTableChange: TableProps<InvitationWithRelationsDto>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    // Convert antd types to our utility function types
    const tableChangeData = {
      pagination,
      filters: filters as Record<string, string[] | null>,
      sorter: {
        field: Array.isArray(sorter) ? sorter[0]?.field : sorter?.field,
        order: Array.isArray(sorter) ? sorter[0]?.order : sorter?.order,
      } as { field?: string | string[]; order?: "ascend" | "descend" },
    }
    handleTableChange(tableChangeData, setQueryParams, columns)
  }

  const displayColumns = useMemo(
    () => applyQueryParamsToColumns(columns, queryParams),
    [columns, queryParams]
  )

  const paginationConfig = getRememberedPaginationConfig(
    response?.meta?.pagination?.total || 0,
    queryParams,
    resetQueryParams
  )

  return (
    <>
      <Table<InvitationWithRelationsDto>
        bordered
        columns={displayColumns}
        dataSource={response?.data || []}
        loading={isLoading}
        rowKey="uuid"
        pagination={paginationConfig}
        locale={getRememberedTableLocale(resetQueryParams, queryParams)}
        onChange={onTableChange}
        onRow={(record) => ({
          onClick: () => handleRowClick(record),
          style: { cursor: onRowClick ? "pointer" : "default" },
        })}
        className={className}
        {...tableProps}
      />
    </>
  )
}
